import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  Car,
  Banknote,
  FileCheck2,
  ShieldCheck,
  Plane,
  Users,
  Star,
  Quote,
} from "lucide-react";

import Hero3D from "@/components/Hero3D";
import SectionHeading from "@/components/SectionHeading";
import VehicleCard from "@/components/VehicleCard";
import EnquiryDialog from "@/components/EnquiryDialog";
import EMICalculator from "@/components/EMICalculator";
import CarVideoShowcase from "@/components/CarVideoShowcase";
import { Button } from "@/components/ui/button";
import { VEHICLES, SERVICES, REASONS, TESTIMONIALS, STATS, BRAND } from "@/lib/data";

const iconMap = { Car, Banknote, FileCheck2, ShieldCheck, Plane, Users };

export default function Home() {
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [preselect, setPreselect] = useState("");

  const openEnquiry = (v = "") => {
    setPreselect(v);
    setEnquiryOpen(true);
  };

  return (
    <div data-testid="home-page">
      <Hero3D onEnquire={() => openEnquiry()} />

      {/* Why Choose */}
      <section className="relative py-24 md:py-32 border-t border-white/5" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading
            eyebrow="Why Be Easy Cars"
            title="Complete T-Permit Vehicle Solutions Under One Roof"
            description="From selecting the right vehicle to yellow-plate registration, finance, insurance and airport attachment — we handle every step so you focus only on driving your business."
          />

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">
            {REASONS.map((reason, i) => (
              <motion.div
                key={reason}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className="glass glass-hover rounded-2xl p-5 flex items-start gap-3"
                data-testid={`why-item-${i}`}
              >
                <CheckCircle2 className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
                <span className="text-sm md:text-base text-slate-200 leading-snug font-medium">
                  {reason}
                </span>
              </motion.div>
            ))}
          </div>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
            {STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-6"
              >
                <div className="absolute inset-0 radial-glow-red opacity-30" />
                <div className="relative">
                  <div className="font-display font-black text-4xl md:text-5xl text-white">{s.value}</div>
                  <div className="text-xs uppercase tracking-wider text-slate-400 mt-2">{s.label}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Vehicles */}
      <section className="relative py-24 md:py-32" data-testid="featured-vehicles-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-red-500 mb-4 font-mono-brand">
                <span className="w-8 h-px bg-current" /> Our Featured Vehicles
              </div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[1.05]">
                Commercial Vehicles Built for <span className="text-gradient-red">Duty</span>
              </h2>
            </div>
            <Link to="/vehicles" data-testid="vehicles-see-all">
              <Button
                variant="outline"
                className="border-white/15 bg-white/5 text-white hover:bg-white/10 rounded-full"
              >
                View All Models
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {VEHICLES.map((v, i) => (
              <VehicleCard
                key={v.slug}
                vehicle={v}
                index={i}
                onEnquire={(name) => openEnquiry(name)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="relative py-24 md:py-32 border-t border-white/5" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading
            eyebrow="Our Services"
            title="End-to-End Commercial Vehicle Support"
            description="One trusted team for sales, finance, RTO, insurance and airport onboarding — so you never chase multiple vendors."
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.map((s, i) => {
              const Icon = iconMap[s.icon] || Car;
              return (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="group relative overflow-hidden rounded-2xl glass glass-hover p-7"
                  data-testid={`service-card-${i}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-red-600/15 border border-red-500/30 flex items-center justify-center mb-5 group-hover:bg-red-600 group-hover:border-red-500 transition-colors">
                    <Icon className="w-5 h-5 text-red-400 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-white mb-2">{s.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Business opportunity strip */}
      <section className="relative py-24 md:py-32 border-t border-white/5" data-testid="business-strip-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="relative rounded-3xl overflow-hidden aspect-[4/3] bg-slate-900">
            <img
              src="https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?auto=format&fit=crop&w=1600&q=80"
              alt="Fleet operations"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#050B24] via-transparent to-red-600/20" />
            <div className="absolute bottom-6 left-6 right-6 glass rounded-2xl p-5">
              <div className="text-xs font-mono-brand uppercase tracking-[0.24em] text-red-400 mb-2">
                Business Opportunity
              </div>
              <div className="font-display text-2xl font-bold text-white">
                Launch or Scale Your Taxi Fleet
              </div>
            </div>
          </div>

          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-red-500 mb-4 font-mono-brand">
              <span className="w-8 h-px bg-current" /> Business Opportunity
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter leading-tight text-white">
              Become a Commercial Taxi Owner
            </h2>
            <p className="mt-5 text-slate-400 text-lg leading-relaxed">
              Whether you're starting with your first commercial vehicle or expanding
              an existing fleet, we guide you through vehicle selection, finance,
              documentation, permits and operational advice.
            </p>

            <ul className="mt-8 space-y-3">
              {[
                "Vehicle selection matched to your route",
                "Finance planning & bank coordination",
                "T-Permit + RTO documentation",
                "Insurance & annual renewal support",
                "Airport & corporate attachment guidance",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-slate-300">
                  <CheckCircle2 className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/business" data-testid="home-business-cta">
                <Button className="bg-red-600 hover:bg-red-700 text-white font-semibold rounded-full px-6 h-12">
                  Explore Opportunity
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Button
                variant="outline"
                onClick={() => openEnquiry()}
                className="border-white/15 bg-white/5 text-white hover:bg-white/10 rounded-full px-6 h-12"
                data-testid="home-business-consult"
              >
                Book Free Consultation
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* EMI Calculator */}
      <section className="relative py-24 md:py-32 border-t border-white/5" data-testid="home-emi-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading
            eyebrow="Plan your Purchase"
            title="EMI Calculator"
            description="Estimate your monthly EMI in real time — then apply for finance with a single click. Actual rates depend on eligibility."
          />
          <EMICalculator />
        </div>
      </section>

      {/* Cinematic Car Video Showcase */}
      <CarVideoShowcase />

      {/* Testimonials */}
      <section className="relative py-24 md:py-32 border-t border-white/5" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading
            eyebrow="Customer Voices"
            title="Trusted by Drivers, Fleet Owners & Entrepreneurs"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="glass rounded-2xl p-6"
                data-testid={`testimonial-${i}`}
              >
                <Quote className="w-6 h-6 text-red-500 mb-4" />
                <p className="text-slate-200 leading-relaxed text-sm mb-5 min-h-[110px]">
                  "{t.quote}"
                </p>
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: t.rating }).map((_, k) => (
                    <Star key={k} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <div className="border-t border-white/10 pt-3">
                  <div className="font-display font-bold text-white">{t.name}</div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider mt-0.5">
                    {t.role} • {t.location}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 md:py-32" data-testid="home-final-cta">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0A1229] via-[#0A1229] to-[#0F1A3A] border border-white/10 p-10 md:p-16">
            <div className="absolute -top-24 -right-24 w-96 h-96 radial-glow-red pointer-events-none" />
            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="font-display text-3xl md:text-5xl font-black tracking-tighter text-white leading-tight">
                  Ready to own your commercial vehicle?
                </h2>
                <p className="mt-4 text-slate-400 text-lg max-w-lg">
                  Talk to a Be Easy Cars expert. No pressure — just clear guidance on
                  vehicle, finance and permit.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 lg:justify-end">
                <Button
                  onClick={() => openEnquiry()}
                  size="lg"
                  className="bg-red-600 hover:bg-red-700 text-white font-bold rounded-full px-8 h-14"
                  data-testid="final-cta-enquire"
                >
                  Get Free Consultation
                </Button>
                <a href={BRAND.whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="final-cta-whatsapp">
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full border-green-500/40 bg-green-500/10 text-green-400 hover:bg-green-500/20 rounded-full px-7 h-14"
                  >
                    WhatsApp Now
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <EnquiryDialog open={enquiryOpen} onOpenChange={setEnquiryOpen} defaultVehicle={preselect} />
    </div>
  );
}
